import React, {Fragment, useEffect, useState} from 'react';
import makeStyles from '@mui/styles/makeStyles';
import {useTranslation} from 'react-i18next';
import API from '../../app/api';
import parse from 'autosuggest-highlight/parse';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import {useDebounce} from 'use-debounce';
import _, {isEmpty} from 'lodash';
import {FormLabel, Grid, IconButton} from '@mui/material';
import AgyleDictionaryField from '../../components/ui/selector/agyleDictionaryField';
import AgyleTextField from '../../components/ui/fields/agyleTextField';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import clsx from 'clsx';
import {useSelector} from 'react-redux';
import {agylePref} from '../../store/reducers/preferences';
import config from '../../app/config';
import {escapeRegExp} from '../../pages/ward/forms/formsUtil';

const useStyles = makeStyles((theme) => ({
    root: {
        backgroundColor: theme.palette.mode === 'light' ? '#ffffff' : '#333333',
        boxShadow: `0px 0px ${theme.palette.mode === 'light' ? 6 : 0}px 0px rgba(0, 0, 0, 0.25)`,
        borderRadius: 5,
        padding: 8,
        '&.border': {
            boxShadow: `0px 0px 6px 0px rgba(0, 0, 0, 0.25)`,
        },
    },
    avatarCustom: {
        width: 'auto !important',
        paddingLeft: 10,
        paddingRight: 10,
        borderRadius: 15,
        backgroundColor: theme.palette.background.paper,
        color: '#fff',
    },
    snomedCode: {
        display: 'inline',
        fontSize: '80%',
        fontWeight: 'bold',
        backgroundColor: theme.palette.background.default,
        padding: '3px 8px',
        marginRight: 10,
        borderRadius: 4,
    },
    snomedDescription: {
        display: 'inline',
    },
    label: {
        color: theme.palette.mode === 'light' ? '#626466' : '#FFFFFF',
        fontWeight: 'bold',
        fontSize: 14,
    },
    divRow: {
        display: 'flex',
        flexWrap: 'nowrap',
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'center',
        paddingLeft: 10,
    },
    inputRoot: {
        minWidth: '100%',
    },
}));

function AutocompleteDictionary(props) {
    const classes = useStyles();
    const {t} = useTranslation();
    const agylePreference = useSelector(agylePref);

    const controller = new AbortController();

    const [autocompleteOpen, setAutocompleteOpen] = React.useState(false);
    const [autocompleteOptions, setAutocompleteOptions] = React.useState([]);
    const [autocompleteInputValue, setAutocompleteInputValue] = React.useState('');
    const [autocompleteLoading, setAutocompleteLoading] = React.useState(false);

    const [autocompleteInputValueDebounced] = useDebounce(autocompleteInputValue, 250);
    const [value, setValue] = useState(props.multiple ? [] : null);

    const renderText = props.renderText;
    const [focus, setFocus] = useState();
    let leaveTimeout;

    useEffect(() => {
        let isMounted = true;

        if (props.showAll || (autocompleteInputValueDebounced && !_.isEmpty(autocompleteInputValueDebounced))) {
            let filter = '';

            if (props.dontTrimSearch) {
                filter = getFilter(autocompleteInputValueDebounced, filter, escapeRegExp);
            } else {
                for (let term of autocompleteInputValueDebounced.split(' ')) {
                    filter = getFilter(term, filter, escapeRegExp);
                }
            }
            if (props.noRegex) {
                filter = autocompleteInputValueDebounced;
            }
            setAutocompleteLoading(true);

            if (_.isNil(props.dataType) || props.dataType === 'API') {

                if (!props.cliniciansWithNurses) {
                    API.getDictByURL(props.dictionaryURL(filter), controller.signal).then(dictData => {
                        if (!isMounted) return;

                        if (dictData?.__data && !_.isEmpty(dictData.__data)) {
                            let data = dictData.__data;

                            if (props.allowedConsultants && config?.consultantComponentEnabled) {
                                data = data.filter(element => {
                                    if (element?.hasOwnProperty('availableIn') && Array.isArray(element.availableIn)) {
                                        if (element.availableIn?.includes(props.allowedConsultants)) {
                                            return element;
                                        }
                                    }
                                });
                            }

                            if (props.filterByIds) {
                                if (!_.isEmpty(props.filterIds)) {
                                    data = data.filter(element => {
                                        return props.filterIds.some((filter) => {
                                            return filter === element.__id;
                                        });
                                    });
                                } else {
                                    data = [];
                                }
                            }
                            setAutocompleteOptions(data);
                        } else {
                            setAutocompleteOptions([]);
                        }
                        setAutocompleteLoading(false);
                    }).catch(function (error) {
                        console.error('Autocomplete dictionaries error', error);
                    });
                } else {
                    getCliniciansWithNurses(filter);
                    setAutocompleteLoading(false);
                }
            } else if (props.dataType === 'admission') {
                API.getDictByURL(props.dictionaryURL(filter), controller.signal).then(dictData => {
                    if (!isMounted) return;

                    if (dictData) {
                        let data = dictData;

                        if (props.allowedConsultants && config?.consultantComponentEnabled) {
                            data = data.filter(element => {
                                if (element?.availableIn.includes(props.allowedConsultants)) {
                                    return element;
                                }
                            });
                        }

                        if (props.filterByIds) {
                            if (!_.isEmpty(props.filterIds)) {
                                data = data.filter(element => {
                                    return props.filterIds.some((filter) => {
                                        return filter === element.__id;
                                    });
                                });
                            } else {
                                data = [];
                            }
                        }
                        setAutocompleteOptions(data);
                    } else {
                        setAutocompleteOptions([]);
                    }
                    //setAutocompleteLoading(false);
                }).catch(function (error) {
                    console.error('Autocomplete dictionaries error', error);
                }).finally(() => {
                    setAutocompleteLoading(false);
                });
            } else {
                setAutocompleteLoading(false);
                setAutocompleteOptions(props.options || []);
            }
        }
        return () => {
            isMounted = false;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [autocompleteInputValueDebounced, props.filterCode, props.filterIds, props.dataType, props.allowedConsultants]);

    useEffect(() => {
        if (props.multiple && _.isNull(props.value)) {
            setValue([]);
        } else {
            setValue(props.value || null);
        }
    }, [props.value]);

    function getCliniciansWithNurses(filter) {
        Promise.all([
                getClinician(filter, 'Clinicians'),
                getClinician(filter, 'Nurses'),
            ])
            .then(([clinicians, nurses]) => {
                const combinedOptions = [];

                if (!_.isEmpty(clinicians)) {
                    combinedOptions.push(...clinicians);
                }

                if (!_.isEmpty(nurses)) {
                    combinedOptions.push(...nurses);
                }
                setAutocompleteOptions(combinedOptions);
            })
            .catch(error => {
                console.error('Autocomplete dictionaries error', error);
            });
    }

    async function getClinician(filter, clinicianType) {
        let medicalStaff = [];
        try {
            const dictData = await API.getDictByURL(props.getClinicianURL(clinicianType, filter), controller.signal);
            if (dictData?.__data && !_.isEmpty(dictData.__data)) {
                let data = dictData.__data;

                if (props.filterByIds) {
                    if (!_.isEmpty(props.filterIds)) {
                        data = data.filter(element => {
                            return props.filterIds.some(filter => {
                                return filter === element.__id;
                            });
                        });
                    } else {
                        data = [];
                    }
                }
                medicalStaff = data.map(clinician => ({
                    ...clinician,
                    group: clinicianType,
                }));
            }
        } catch (error) {
            console.error(error);
        }
        return medicalStaff;
    }

    // Get input search
    function getFilter(input, filter) {
        if (!_.isEmpty(input)) {
            if (!_.isEmpty(filter)) filter += ' AND ';
            if (props.searchColumnName) {
                filter += `${props.searchColumnName} regex '.*` + escapeRegExp(input) + `.*'`;
            }
            if (props.multiSearchColumnName) {
                for (let i = 0; i < props.multiSearchColumnName?.length; i++) {
                    if (i > 0) {
                        filter += ' or ';
                    }
                    filter += `${props.multiSearchColumnName[i]} regex '.*` + escapeRegExp(input) + `.*'`;
                }
            }
        }
        return filter;
    }

    const testForNotApplicable = (obj, renderTextForce) => {
        // if (obj[renderTextForce] === 'Not applicable to max terms') {
        //     return obj.__description;
        // }
        return obj[renderTextForce];
    };

    const renderOptionLine = (createParts, option, classes, props, type) => {
        let obj = option;
        let renderTextForce = renderText;
        if (obj?.ecdsVersion === 'V4' && type === 'Diagnosis') {
            const find = _.find(value, val => val?.value?.diagnosisMax && val?.value?.usedParentSnomedCode === obj?.snomedCode);

            if (find) {
                obj = find?.value;
                renderTextForce = 'snomedFullySpecifiedName';
            }
        }

        const parts = createParts(renderTextForce ? testForNotApplicable(obj, renderTextForce) : obj.__description);
        return (
            <li {...props}>
                <Grid container direction='row'>
                    {(agylePreference?.showSnomedCode && obj?.snomedCode) &&
                        <div className={classes.snomedCode}>
                            {obj.snomedCode}
                        </div>
                    }

                    <div className={classes.snomedDescription}>
                        {parts.map((part, index) => (
                            <span key={index} style={{
                                textDecoration: part.highlight ? 'underline' : 'none',
                                fontWeight: part.highlight ? 700 : 400,
                            }}>
                            {part.text}
                        </span>
                        ))}
                    </div>
                </Grid>
            </li>
        );
    };

    const createParts = optionValue => {
        let matches = [];
        if (optionValue && autocompleteInputValueDebounced && !_.isEmpty(autocompleteInputValueDebounced)) {
            let idx = optionValue.toLowerCase().indexOf(autocompleteInputValueDebounced.toLowerCase());
            while (idx >= 0) {
                matches.push([idx, idx + autocompleteInputValueDebounced.length]);
                idx = optionValue.toLowerCase().indexOf(autocompleteInputValueDebounced.toLowerCase(), idx + autocompleteInputValueDebounced.length);
            }
        }
        return parse(optionValue, matches);
    };

    const renderLocalOption = (option, inp) => {
        if (props.renderLocalOption) {
            return props.renderLocalOption(createParts, option, classes, inp, 'external', autocompleteOptions);
        } else {
            return renderOptionLine(createParts, option, classes, inp, props.type);
        }
    };

    const handleChange = (_event, newValues, reason) => {
        if (props.onChangeAdvanced) {
            props.onChangeAdvanced(newValues, reason);
            setAutocompleteOpen(false);
        } else {
            props.onChange(newValues);
        }
    };

    const renderAvatar = (option) => {
        if (!props.withoutAvatar && agylePreference?.showSnomedCode) {
            return (
                <div style={{
                    lineHeight: props.size === 'small' ? '18px' : '24px',
                    color: '#fff',
                    fontWeight: 'normal',
                }}
                     className={classes.avatarCustom}
                >
                    {option.snomedCode}
                </div>
            );
        }
        return null;
    };

    const openAutocomplete = () => {
        if (!autocompleteOpen && !props.disabled) {
            setAutocompleteOpen(true);
            setTimeout(() => {
                focus?.focus();
            }, 10);
        }
    };

    const handleLeave = (e) => {
        if (autocompleteOpen && e?.pointerType !== 'touch') {
            leaveTimeout = setTimeout(() => {
                setAutocompleteOpen(false);
            }, 1000);
        }
    };
    const handleEnter = () => {
        clearTimeout(leaveTimeout);
    };

    const handleAutocompleteState = (val) => {
        setAutocompleteOpen(val);
    };

    return (
        <div
            className={clsx(props.extended && classes.root, props.withBorder && 'border')}
            onPointerLeave={handleLeave}
            onPointerEnter={handleEnter}
        >
            {props.extended &&
                <div
                    className={classes.divRow}
                    onClick={openAutocomplete}
                >
                    <FormLabel className={classes.label}>
                        {props.label}
                    </FormLabel>

                    <div>
                        {props.endAdornmentAddons && props.endAdornmentAddons()}

                        <IconButton
                            disableRipple
                            disabled={props.disabled}
                            color="primary"
                        >
                            <AddCircleOutlineIcon/>
                        </IconButton>
                    </div>
                </div>
            }

            {(!props.extended || (props.extended && !_.isEmpty(value)) || autocompleteOpen) &&
                <AgyleDictionaryField
                    focusInput={setFocus}
                    multiple={props.multiple}
                    disableCloseOnSelect={props.disableCloseOnSelect}
                    size={props.size}
                    label={!props.extended ? props.label : ''}
                    color={props.color}
                    textFieldClass={props.textFieldClass}
                    className={props.className || null}
                    open={autocompleteOpen}
                    openOnFocus={props.extended}
                    onOpen={handleAutocompleteState.bind(this, true)}
                    onClose={handleAutocompleteState.bind(this, false)}
                    onBlur={handleAutocompleteState.bind(this, false)}
                    limitTags={props.limitTags}
                    bigLabel={props.bigLabel}
                    disabled={props.disabled || props.loadingState}
                    isOptionEqualToValue={(option, value) => props.isOptionEqualToValue
                        ? props.isOptionEqualToValue(option, value)
                        : option?.snomedDescription === value?.snomedDescription}
                    getOptionLabel={(option) => props.getOptionLabel ? props.getOptionLabel(option) : option.snomedDescription}
                    options={autocompleteOptions}
                    loading={autocompleteLoading}
                    filterOptions={(x) => x}
                    groupBy={props.groupBy}
                    noOptionsText={!isEmpty(autocompleteInputValueDebounced) ? t('Nothing found') : t('Please enter at least one letter')}
                    placeholder={props.placeholder}
                    loadingText={t('Loading data ...')}
                    onInputChange={(event, newInputValue) => setAutocompleteInputValue(newInputValue)}
                    value={value}
                    onChange={handleChange}
                    getOptionDisabled={props.getOptionDisabled ? props.getOptionDisabled : () => {
                        return false;
                    }}
                    required={props.required}
                    disableClearable={props.disableClearable}
                    paperBackground={props.paperBackground}
                    withBackground={props.withBackground}
                    noBorder={props.noBorder}
                    error={props.error}
                    popupIcon={props.popupIcon}
                    endAdornment={props.endAdornment}
                    clearIcon={props.clearIcon}
                    // endAdornmentAddons={props.endAdornmentAddons}
                    extended={props.extended}
                    withNewBackground={props.withNewBackground}
                    renderInput={(params) => (
                        <div style={{position: 'relative'}}>
                            <AgyleTextField
                                {...params}
                                error={props.error}
                                size={props.size}
                                label={props.label}
                                color={props.color}
                                textFieldClass={clsx(props.extended && classes.textBackground, props.inputClass)}
                                variant="outlined"
                                InputProps={{
                                    ...props.inputProps,
                                    ...params.InputProps,
                                    startAdornment: props.startTextBoxAdornment ? props.startTextBoxAdornment() : params.InputProps.startAdornment,
                                    endAdornment: (
                                        <Fragment>
                                            {/*{props.endAdornmentAddons && props.endAdornmentAddons()}*/}
                                            {autocompleteLoading ? <CircularProgress color="inherit" size={20}/> : null}
                                            {params.InputProps.endAdornment}
                                        </Fragment>
                                    ),
                                }}
                            />

                            {props.loadingState &&
                                <CircularProgress style={{position: 'absolute', left: '50%', top: '15%'}}/>
                            }
                        </div>
                    )}
                    startTextBoxAdornment={props.startTextBoxAdornment}
                    renderTags={(tagValue, getTagProps) => props.renderTags ? props.renderTags(tagValue, getTagProps)
                        : tagValue.map((option, index) => (
                            <Chip
                                color={props.color}
                                size={props.size}
                                label={props.showColumnText ? option[props.showColumnText] : option.snomedDescription}
                                style={{
                                    fontWeight: 'bold',
                                }}
                                {...getTagProps({index})}
                                avatar={renderAvatar(option)}
                            />
                        ))
                    }
                    renderOption={(props, option, {selected}) => renderLocalOption(option, props)}
                />
            }
        </div>
    );
}

export {AutocompleteDictionary};
